<script setup lang="ts">
import { FairIcon } from '@/ui/primitives/FairIcon'
import type { FairIconName } from '@/ui/tokens/icons.data'

/**
 * Bottomnav bar — DS "Page-level" › Bottomnav bar (1198:49794), mobile only (<1024).
 *
 * A white plate with the `Subtract` notch at the top centre; the AIRA ball docks into it
 * (rendered elsewhere — this component only leaves the gap). Two icon groups, no labels,
 * a 4px dot under the current tab. Presentational: routing, auth and hide-on-scroll live
 * in `features/bottom-nav`.
 *
 * FE-017 icon emphasis (house lands, hanger swings, heart beats, head nods) is driven by
 * `motion` per tab and is an agreed exception to the Foundations motion timings.
 */
export interface FairBottomNavItem {
  id: string
  label: string
  icon: FairIconName
  /** FairIcon path groups, so separate parts can animate (the head and shoulders) */
  parts?: number[][]
  motion?: 'settle' | 'swing' | 'beat' | 'nod'
}

interface FairBottomNavProps {
  startItems: readonly FairBottomNavItem[]
  endItems: readonly FairBottomNavItem[]
  activeId?: string | null
  hidden?: boolean
  ariaLabel?: string
}

withDefaults(defineProps<FairBottomNavProps>(), {
  activeId: null,
  hidden: false,
  ariaLabel: undefined,
})

const emit = defineEmits<{
  select: [item: FairBottomNavItem]
}>()
</script>

<template>
  <nav
    class="fair-bottom-nav"
    :class="{ 'fair-bottom-nav--hidden': hidden }"
    :aria-label="ariaLabel"
    :aria-hidden="hidden ? 'true' : undefined"
    :inert="hidden || undefined"
    data-figma="1198:49794"
  >
    <div class="fair-bottom-nav__plate">
      <!-- Figma `Subtract`: plate 68 high, r20, minus a smooth notch (S-shoulders, 164 wide, 62 deep).
           Three pieces so the notch keeps its exact curve at any viewport width. -->
      <div class="fair-bottom-nav__shape" aria-hidden="true">
        <div class="fair-bottom-nav__shape-side" />
        <div class="fair-bottom-nav__shape-notch">
          <svg
            class="fair-bottom-nav__shape-svg"
            viewBox="0 0 164 68"
            width="164"
            height="68"
            preserveAspectRatio="none"
          >
            <path d="M0 0C11.046 0 19.682 9.185 23.182 19.6614C31.403 44.2657 54.631 62 82 62C109.369 62 132.597 44.2657 140.818 19.6614C144.318 9.185 152.954 0 164 0V68H0Z" />
          </svg>
          <div class="fair-bottom-nav__shape-fill" />
        </div>
        <div class="fair-bottom-nav__shape-side" />
      </div>

      <div
        v-for="(group, index) in [startItems, endItems]"
        :key="index"
        class="fair-bottom-nav__group"
        :class="{ 'fair-bottom-nav__group--end': index === 1 }"
      >
        <button
          v-for="item in group"
          :key="item.id"
          type="button"
          class="fair-bottom-nav__tab"
          :class="{ 'fair-bottom-nav__tab--active': item.id === activeId }"
          :aria-label="item.label"
          :aria-current="item.id === activeId ? 'page' : undefined"
          :data-tab="item.id"
          :data-motion="item.motion"
          @click="emit('select', item)"
        >
          <span class="fair-bottom-nav__glyph">
            <FairIcon
              :name="item.icon"
              :size="20"
              :parts="item.parts"
              class="fair-bottom-nav__icon"
            />
          </span>
          <span class="fair-bottom-nav__dot" />
        </button>
      </div>

      <!-- The AIRA ball docks above this gap; nothing is drawn here. -->
      <div class="fair-bottom-nav__center" aria-hidden="true" />
    </div>
  </nav>
</template>
